import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { toast } from "sonner";
import { Truck, Lock, User } from "lucide-react";
import api from "../lib/api";

const LOGO_URL = "/nassau-logo.png";

export default function LoginPage() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  // Initialize admin on first load
  useState(() => {
    api.post("/init").catch(() => {});
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const user = await login(username, password);
      toast.success(`Welcome, ${user.full_name}!`);
      navigate(user.role === "admin" ? "/admin" : "/driver");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-900 p-4">
      <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900"></div>
      
      <Card className="w-full max-w-md relative z-10 border-slate-700 bg-slate-800/90 backdrop-blur-sm">
        <CardHeader className="text-center space-y-4 pb-2">
          <div className="mx-auto w-48 h-auto">
            <img src={LOGO_URL} alt="Nassau Engineering Ltd." className="w-full h-auto" />
          </div>
          <CardTitle className="text-2xl font-bold text-white">DeliverIQ</CardTitle>
          <CardDescription className="text-slate-400">
            Sign in to access your delivery dashboard
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="username" className="text-slate-300">Username</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-500" />
                <Input
                  id="username"
                  data-testid="login-username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Enter your username"
                  className="pl-10 h-12 bg-slate-700 border-slate-600 text-white placeholder:text-slate-500 focus:border-[#D32F2F] focus:ring-[#D32F2F]"
                  required
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-300">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-500" />
                <Input
                  id="password"
                  data-testid="login-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="pl-10 h-12 bg-slate-700 border-slate-600 text-white placeholder:text-slate-500 focus:border-[#D32F2F] focus:ring-[#D32F2F]"
                  required
                />
              </div>
            </div>
            
            <Button
              type="submit"
              data-testid="login-submit"
              disabled={loading}
              className="w-full h-12 bg-[#D32F2F] hover:bg-[#B71C1C] text-white font-semibold text-lg"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></span>
                  Signing in...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Truck className="h-5 w-5" />
                  Sign In
                </span>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
